/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commerical BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2019-10-29 07:46:53
 *
 *******************************************************/
// @flow

import _ from 'lodash';
import moment from 'moment-timezone';
import React, { Component } from 'react';
import { connect } from 'react-redux';
import { Layout, Banner } from '@shopify/polaris';
import { withI18n, WithI18nProps } from '@shopify/react-i18n';

import Filter from '../filter';
import TopFilterOptionValues from './top-filter-option-values';
import TopFilterOptionValueCombinations from './top-filter-option-value-combinations';
import TopTotalFilterClicks from './top-total-filter-clicks';
import TotalSales from './total-sales';

import { getStoreDate } from 'src/helpers/analytics-helpers';
import { buildQueryKey, findExecutionId } from 'src/helpers/analytics-query-execution-id-helper';
import { QUERY_EXECUTION_KEY } from 'src/constants/analytics';
import { getUTCDate } from 'src/helpers/date-time-helper';

import * as AnalyticsActions from 'src/stores/actions/analytics-actions';
import * as AnalyticsQueryExecutionIdActions from 'src/stores/actions/analytics-query-execution-id-actions';

type Props = WithI18nProps;
class Collections extends Component<Props> {
  state = {
    blockDetailId: -1
  };

  componentDidMount() {
    const today = getStoreDate(new Date());
    const {
      compare,
      fromDate = today,
      toDate = today,
      collectionId,
      showOrderRevenue
    } = this.props;

    // Initial data, from date and to date are current date
    // Get top collections total
    // Get from date, to date with store timezone
    const fromDateUTC = getUTCDate(fromDate, 0, 0, 0);
    const toDateUTC = getUTCDate(toDate, 24, 0, 0);

    if (collectionId === 'all') {
      this.getTopCollectionsTotalFilterClicks(fromDateUTC, toDateUTC, compare);
      if (showOrderRevenue) {
        this.getTopCollectionsTotalSales(fromDateUTC, toDateUTC, compare);
      }
    }

    // Get collection total by time
    this.getCollectionTotalDetailsByTimeFilterClicks(fromDateUTC, toDateUTC, compare, collectionId);

    // Get top filter option values
    this.getTopFilterOptionValues(fromDateUTC, toDateUTC, compare, collectionId);

    // Get top filter option value combination
    this.getTopFilterOptionValueCombinations(fromDateUTC, toDateUTC, compare, collectionId);

    // Get collection total sale by time
    if (showOrderRevenue) {
      this.getTotalSales(fromDateUTC, toDateUTC, compare, collectionId);
    }
  }

  componentWillReceiveProps(nextProps) {
    const { fromDate, toDate, compare, collectionId } = this.props;
    const {
      fromDate: nextFromDate,
      toDate: nextToDate,
      compare: nextCompare,
      collectionId: nextCollectionId,
      showOrderRevenue
    } = nextProps;

    // Check changing filter
    if (
      fromDate !== nextFromDate ||
      toDate !== nextToDate ||
      compare !== nextCompare ||
      collectionId !== nextCollectionId
    ) {
      // Get from date, to date with store timezone
      const fromDateUTC = getUTCDate(nextFromDate, 0, 0, 0);
      const toDateUTC = getUTCDate(nextToDate, 24, 0, 0);
      // Get top collections total
      if (nextCollectionId === 'all') {
        this.getTopCollectionsTotalFilterClicks(fromDateUTC, toDateUTC, nextCompare);
        if (showOrderRevenue) {
          this.getTopCollectionsTotalSales(fromDateUTC, toDateUTC, nextCompare);
        }
      }
      // Get collection total by time
      this.getCollectionTotalDetailsByTimeFilterClicks(
        fromDateUTC,
        toDateUTC,
        nextCompare,
        nextCollectionId
      );

      // Get top filter option values
      this.getTopFilterOptionValues(fromDateUTC, toDateUTC, nextCompare, nextCollectionId);

      // Get top filter option value combination
      this.getTopFilterOptionValueCombinations(
        fromDateUTC,
        toDateUTC,
        nextCompare,
        nextCollectionId
      );

      // Get collection total by time
      if (showOrderRevenue) {
        this.getTotalSales(fromDateUTC, toDateUTC, nextCompare, nextCollectionId);
      }
    }

    // Update execution ids
    this.processUpdateExecutionId(this.props, nextProps);
  }

  processUpdateExecutionId = (props, nextProps) => {
    const { showOrderRevenue } = props;
    this.processExecutionId_TopFilterOptionCombinations(props, nextProps);

    this.processExecutionId_TopFilerOptionValues(props, nextProps);

    this.processExecutionId_TotalFilterClicks(props, nextProps);
    if (showOrderRevenue) {
      this.processExecutionId_TotalSales(props, nextProps);
    }
  };

  processExecutionId_TopFilterOptionCombinations = (props, nextProps) => {
    const { topFilterOptionValueCombinations_QueryExecutionId } = props;
    const {
      fromDate: nextFromDate,
      toDate: nextToDate,
      compare: nextCompare,
      collectionId: nextCollectionId,
      topFilterOptionValueCombinations_QueryExecutionId:
        topFilterOptionValueCombinations_QueryExecutionIdNext
    } = nextProps;

    // Build query key
    const queryKey = buildQueryKey({
      fromDate: getUTCDate(nextFromDate, 0, 0, 0),
      toDate: getUTCDate(nextToDate, 24, 0, 0),
      compare: nextCompare,
      collectionId: nextCollectionId
    });

    // Update execution id
    if (
      !_.isEmpty(topFilterOptionValueCombinations_QueryExecutionIdNext) &&
      topFilterOptionValueCombinations_QueryExecutionId !==
        topFilterOptionValueCombinations_QueryExecutionIdNext
    ) {
      this.props.updateExecutionId({
        queryKey,
        executionIdKey: QUERY_EXECUTION_KEY.TOP_FILTER_OPTION_COMBINATIONS,
        executionId: topFilterOptionValueCombinations_QueryExecutionIdNext
      });
    }
  };

  processExecutionId_TopFilerOptionValues = (props, nextProps) => {
    const { topFilterOptionValues_QueryExecutionId } = props;
    const {
      fromDate: nextFromDate,
      toDate: nextToDate,
      compare: nextCompare,
      collectionId: nextCollectionId,
      topFilterOptionValues_QueryExecutionId: topFilterOptionValues_QueryExecutionIdNext
    } = nextProps;

    // Build query key
    const queryKey = buildQueryKey({
      fromDate: getUTCDate(nextFromDate, 0, 0, 0),
      toDate: getUTCDate(nextToDate, 24, 0, 0),
      compare: nextCompare,
      collectionId: nextCollectionId
    });

    // Update execution id
    if (
      !_.isEmpty(topFilterOptionValues_QueryExecutionIdNext) &&
      topFilterOptionValues_QueryExecutionId !== topFilterOptionValues_QueryExecutionIdNext
    ) {
      this.props.updateExecutionId({
        queryKey,
        executionIdKey: QUERY_EXECUTION_KEY.TOP_FILTER_OPTION_COLLECTION_PAGE,
        executionId: topFilterOptionValues_QueryExecutionIdNext
      });
    }
  };

  processExecutionId_TotalFilterClicks = (props, nextProps) => {
    const {
      topCollectionsTotalFilterClicks_QueryExecutionId,
      totalFilterClicks_QueryExecutionId,
      totalFilterClicksCompared_QueryExecutionId
    } = props;

    const {
      fromDate: nextFromDate,
      toDate: nextToDate,
      compare: nextCompare,
      collectionId: nextCollectionId,
      topCollectionsTotalFilterClicks_QueryExecutionId:
        topCollectionsTotalFilterClicks_QueryExecutionIdNext,
      totalFilterClicks_QueryExecutionId: totalFilterClicks_QueryExecutionIdNext,
      totalFilterClicksCompared_QueryExecutionId: totalFilterClicksCompared_QueryExecutionIdNext
    } = nextProps;

    // Build query key
    const queryKey = buildQueryKey({
      fromDate: getUTCDate(nextFromDate, 0, 0, 0),
      toDate: getUTCDate(nextToDate, 24, 0, 0),
      compare: nextCompare,
      collectionId: nextCollectionId
    });

    // Update execution id
    if (
      !_.isEmpty(topCollectionsTotalFilterClicks_QueryExecutionIdNext) &&
      topCollectionsTotalFilterClicks_QueryExecutionId !==
        topCollectionsTotalFilterClicks_QueryExecutionIdNext
    ) {
      this.props.updateExecutionId({
        queryKey,
        executionIdKey: QUERY_EXECUTION_KEY.TOP_COLLECTIONS_TOTAL_FILTER_CLICKS,
        executionId: topCollectionsTotalFilterClicks_QueryExecutionIdNext
      });
    }

    if (
      !_.isEmpty(totalFilterClicks_QueryExecutionIdNext) &&
      totalFilterClicks_QueryExecutionId !== totalFilterClicks_QueryExecutionIdNext
    ) {
      this.props.updateExecutionId({
        queryKey,
        executionIdKey: QUERY_EXECUTION_KEY.TOTAL_FILTER_CLICKS,
        executionId: totalFilterClicks_QueryExecutionIdNext
      });
    }

    if (
      !_.isEmpty(totalFilterClicksCompared_QueryExecutionIdNext) &&
      totalFilterClicksCompared_QueryExecutionId !== totalFilterClicksCompared_QueryExecutionIdNext
    ) {
      this.props.updateExecutionId({
        queryKey,
        executionIdKey: QUERY_EXECUTION_KEY.TOTAL_FILTER_CLICKS_COMPARED,
        executionId: totalFilterClicksCompared_QueryExecutionIdNext
      });
    }
  };

  processExecutionId_TotalSales = (props, nextProps) => {
    const {
      topCollectionsTotalSales_QueryExecutionId,
      totalSales_QueryExecutionId,
      totalSalesCompared_QueryExecutionId
    } = props;

    const {
      fromDate: nextFromDate,
      toDate: nextToDate,
      compare: nextCompare,
      collectionId: nextCollectionId,
      topCollectionsTotalSales_QueryExecutionId: topCollectionsTotalSales_QueryExecutionIdNext,
      totalSales_QueryExecutionId: totalSales_QueryExecutionIdNext,
      totalSalesCompared_QueryExecutionId: totalSalesCompared_QueryExecutionIdNext
    } = nextProps;

    // Build query key
    const queryKey = buildQueryKey({
      fromDate: getUTCDate(nextFromDate, 0, 0, 0),
      toDate: getUTCDate(nextToDate, 24, 0, 0),
      compare: nextCompare,
      collectionId: nextCollectionId
    });

    // Update execution id
    if (
      !_.isEmpty(topCollectionsTotalSales_QueryExecutionIdNext) &&
      topCollectionsTotalSales_QueryExecutionId !== topCollectionsTotalSales_QueryExecutionIdNext
    ) {
      this.props.updateExecutionId({
        queryKey,
        executionIdKey: QUERY_EXECUTION_KEY.TOP_COLLECTIONS_TOTAL_SALES,
        executionId: topCollectionsTotalSales_QueryExecutionIdNext
      });
    }

    if (
      !_.isEmpty(totalSales_QueryExecutionIdNext) &&
      totalSales_QueryExecutionId !== totalSales_QueryExecutionIdNext
    ) {
      this.props.updateExecutionId({
        queryKey,
        executionIdKey: QUERY_EXECUTION_KEY.TOTAL_SALES,
        executionId: totalSales_QueryExecutionIdNext
      });
    }

    if (
      !_.isEmpty(totalSalesCompared_QueryExecutionIdNext) &&
      totalSalesCompared_QueryExecutionId !== totalSalesCompared_QueryExecutionIdNext
    ) {
      this.props.updateExecutionId({
        queryKey,
        executionIdKey: QUERY_EXECUTION_KEY.TOTAL_SALES_COMPARED,
        executionId: totalSalesCompared_QueryExecutionIdNext
      });
    }
  };

  // Calculation position for each block in display list
  // Sorting them in to 4 component
  buildPositionForBlocks = lengthOfList => {
    const { blockDetailId } = this.state;
    let positions = [];
    for (let i = 0; i < lengthOfList; i++) {
      if (i < blockDetailId) {
        positions.push(i % 2);
      }
      if (i === blockDetailId) {
        positions.push(-1);
      }
      if (i > blockDetailId) {
        // const adjustValue = blockDetailId === -1 ? 1 : blockDetailId
        positions.push(((i - blockDetailId + 1) % 2) + 2);
      }
    }
    return positions;
  };

  render() {
    const { blockDetailId } = this.state;
    const { fromDate, showOrderRevenue, performActionSurvey, i18n } = this.props;
    let listComponents = showOrderRevenue
      ? [TotalSales, TopTotalFilterClicks, TopFilterOptionValueCombinations, TopFilterOptionValues]
      : [TopTotalFilterClicks, TopFilterOptionValueCombinations, TopFilterOptionValues];

    const changeShowHideDetail = this.changeShowHideDetail;

    const blockPosition = this.buildPositionForBlocks(listComponents.length);

    // Check the date range has day before 1/3/2020
    const firstMarchDate = getStoreDate(new Date(2020, 2, 1));
    const beforeMarch = moment(firstMarchDate).diff(moment(fromDate), 'day') >= 0;

    const renderBlocksList = position => {
      return (
        <Layout.Section oneHalf={true}>
          {listComponents.map((component, index) => {
            return blockPosition[index] === position
              ? React.createElement(component, {
                  key: index,
                  id: index,
                  blockDetailId,
                  changeShowHideDetail
                })
              : '';
          })}
        </Layout.Section>
      );
    };

    return (
      <div className="BC-Analytics__Collections">
        <Filter isCollection={true} performActionSurvey={performActionSurvey} />
        <div className="BC-Analytics__Collections-Content">
          <Layout>
            {/* Banner info for date range which has day before 01/03*/}
            {beforeMarch ? (
              <Layout.Section>
                <Banner status="info">
                  <p>
                    {i18n.translate('AdminHTML.Analytics.Common.beforeMarchBanner_Content', {
                      t: (
                        <strong>
                          {i18n.translate('AdminHTML.Analytics.Common.beforeMarchBanner_Content_t')}
                        </strong>
                      )
                    })}
                  </p>
                </Banner>
              </Layout.Section>
            ) : (
              ''
            )}

            {/* Render block top left */}
            {renderBlocksList(0)}

            {/* Render block top right */}
            {renderBlocksList(1)}

            {/* Render block which is detail*/}
            {blockDetailId !== -1 ? (
              <Layout.Section>
                {React.createElement(listComponents[blockDetailId], {
                  id: blockDetailId,
                  blockDetailId,
                  changeShowHideDetail
                })}
              </Layout.Section>
            ) : (
              ''
            )}

            {/* Render block bottom left */}
            {renderBlocksList(2)}

            {/* Render block bottom right */}
            {renderBlocksList(3)}
          </Layout>
        </div>
      </div>
    );
  }

  /*
    Get top filter option combinations
   */
  getTopFilterOptionValueCombinations = (fromDate, toDate, compare, collectionId) => {
    const { executionIds } = this.props;

    // Build query key
    const queryKey = buildQueryKey({ fromDate, toDate, compare, collectionId });

    // Get execution ids if existing
    const queryExecutionId = findExecutionId(
      queryKey,
      QUERY_EXECUTION_KEY.TOP_FILTER_OPTION_COMBINATIONS,
      executionIds
    );

    this.props.getTopFilterOptionValueCombinations(
      fromDate,
      toDate,
      compare,
      collectionId,
      queryExecutionId
    );
  };

  /*
    Get top filter option values
   */
  getTopFilterOptionValues = (fromDate, toDate, compare, collectionId) => {
    const { executionIds } = this.props;

    // Build query key
    const queryKey = buildQueryKey({ fromDate, toDate, compare, collectionId });

    // Get execution ids if existing
    const queryExecutionId = findExecutionId(
      queryKey,
      QUERY_EXECUTION_KEY.TOP_FILTER_OPTION_COLLECTION_PAGE,
      executionIds
    );

    this.props.getTopFilterOptionValues(fromDate, toDate, compare, collectionId, queryExecutionId);
  };

  /**
   * Get top collections for total filter clicks, apply execution id
   */
  getTopCollectionsTotalFilterClicks = (fromDate, toDate, compare, collectionId) => {
    const { executionIds } = this.props;

    // Build query key
    const queryKey = buildQueryKey({ fromDate, toDate, compare, collectionId });

    // Get execution id if existing
    const queryExecutionId = findExecutionId(
      queryKey,
      QUERY_EXECUTION_KEY.TOP_COLLECTIONS_TOTAL_FILTER_CLICKS,
      executionIds
    );

    this.props.getTopCollectionsTotalFilterClicks(fromDate, toDate, compare, queryExecutionId);
  };

  /**
   * Get total filter clicks
   */
  getCollectionTotalDetailsByTimeFilterClicks = (fromDate, toDate, compare, collectionId) => {
    const { executionIds } = this.props;

    // Build query key
    const queryKey = buildQueryKey({ fromDate, toDate, compare, collectionId });

    // Get execution ids if existing
    const queryExecutionId = findExecutionId(
      queryKey,
      QUERY_EXECUTION_KEY.TOTAL_FILTER_CLICKS,
      executionIds
    );
    const comparedExecutionId = findExecutionId(
      queryKey,
      QUERY_EXECUTION_KEY.TOTAL_FILTER_CLICKS_COMPARED,
      executionIds
    );

    this.props.getCollectionTotalDetailsByTimeFilterClicks(
      fromDate,
      toDate,
      compare,
      collectionId,
      queryExecutionId,
      comparedExecutionId
    );
  };

  /**
   * Get top collections for total sale, apply execution id
   */
  getTopCollectionsTotalSales = (fromDate, toDate, compare, collectionId) => {
    const { executionIds } = this.props;

    // Build query key
    const queryKey = buildQueryKey({ fromDate, toDate, compare, collectionId });

    // Get execution id if existing
    const queryExecutionId = findExecutionId(
      queryKey,
      QUERY_EXECUTION_KEY.TOP_COLLECTIONS_TOTAL_SALES,
      executionIds
    );

    this.props.getTopCollectionsTotalSales(fromDate, toDate, compare, queryExecutionId);
  };

  /**
   * Get total sales
   */
  getTotalSales = (fromDate, toDate, compare, collectionId) => {
    const { executionIds } = this.props;

    // Build query key
    const queryKey = buildQueryKey({ fromDate, toDate, compare, collectionId });

    // Get execution ids if existing
    const queryExecutionId = findExecutionId(
      queryKey,
      QUERY_EXECUTION_KEY.TOTAL_SALES,
      executionIds
    );
    const comparedExecutionId = findExecutionId(
      queryKey,
      QUERY_EXECUTION_KEY.TOTAL_SALES_COMPARED,
      executionIds
    );

    this.props.getTotalSales(
      fromDate,
      toDate,
      compare,
      collectionId,
      queryExecutionId,
      comparedExecutionId
    );
  };

  changeShowHideDetail = id => {
    const { blockDetailId } = this.state;
    const { performActionSurvey } = this.props;

    const value = id === blockDetailId ? -1 : id;

    // Show action survey when click view detail
    if (value !== -1) {
      performActionSurvey();
    }
    this.setState({ blockDetailId: value });
  };
}

const mapDispatchToProps = (dispatch, ownProps) => ({
  getTopCollectionsTotalFilterClicks: (fromDate, toDate, compare, queryExecutionId) =>
    dispatch(
      AnalyticsActions.getTopCollectionsTotalFilterClicks(
        fromDate,
        toDate,
        compare,
        queryExecutionId,
        ownProps.i18n
      )
    ),

  getCollectionTotalDetailsByTimeFilterClicks: (
    fromDate,
    toDate,
    compare,
    collectionId,
    queryExecutionId,
    comparedExecutionId
  ) =>
    dispatch(
      AnalyticsActions.getCollectionTotalDetailsByTimeFilterClicks(
        fromDate,
        toDate,
        compare,
        collectionId,
        queryExecutionId,
        comparedExecutionId,
        ownProps.i18n
      )
    ),
  getTopFilterOptionValues: (fromDate, toDate, compare, collectionId, queryExecutionId) =>
    dispatch(
      AnalyticsActions.getTopFilterOptionValues(
        fromDate,
        toDate,
        compare,
        collectionId,
        queryExecutionId,
        ownProps.i18n
      )
    ),

  getTopFilterOptionValueCombinations: (
    fromDate,
    toDate,
    compare,
    collectionId,
    queryExecutionId
  ) =>
    dispatch(
      AnalyticsActions.getTopFilterOptionValueCombinations(
        fromDate,
        toDate,
        compare,
        collectionId,
        queryExecutionId,
        ownProps.i18n
      )
    ),

  getTopCollectionsTotalSales: (fromDate, toDate, compare, queryExecutionId) =>
    dispatch(
      AnalyticsActions.getTopCollectionsTotalSales(
        fromDate,
        toDate,
        compare,
        queryExecutionId,
        ownProps.i18n
      )
    ),

  getTotalSales: (fromDate, toDate, compare, collectionId, queryExecutionId, comparedExecutionId) =>
    dispatch(
      AnalyticsActions.getTotalSales(
        fromDate,
        toDate,
        compare,
        collectionId,
        queryExecutionId,
        comparedExecutionId,
        ownProps.i18n
      )
    ),
  updateExecutionId: data => dispatch(AnalyticsQueryExecutionIdActions.updateExecutionId(data))
});

const mapStateToProps = (state, props) => {
  const themeSetupV2 = _.get(state.oauth, 'tenantApp.adminAppFeatures.themeSetupV2', false);
  const showRevenueTrackingV1 = _.get(
    state.oauth,
    'tenantApp.adminAppFeatures.showRevenueTrackingV1',
    false
  );

  return {
    fromDate: _.get(state.analytics, 'fromDate'),
    toDate: _.get(state.analytics, 'toDate'),
    compare: _.get(state.analytics, 'compare', false),
    collectionId: _.get(state.analytics, 'collection.value', 'all'),

    // Show/hide order revenue by themeSetupV2 and showRevenueTrackingV1
    showOrderRevenue: themeSetupV2 || showRevenueTrackingV1,

    // Query execution ids
    executionIds: _.get(state.analyticsQueryExecutionId, 'list', []),

    // Filter option value combinations
    topFilterOptionValueCombinations_QueryExecutionId: _.get(
      state.analytics,
      'topFilterOptionValueCombinationsQueryExecutionId',
      ''
    ),

    // Top filter option values
    topFilterOptionValues_QueryExecutionId: _.get(
      state.analytics,
      'topFilterOptionValuesQueryExecutionId',
      ''
    ),

    // Total filter clicks on collection page
    topCollectionsTotalFilterClicks_QueryExecutionId: _.get(
      state.analytics,
      'topCollectionsTotalFilterClicksQueryExecutionId',
      ''
    ),
    totalFilterClicks_QueryExecutionId: _.get(
      state.analytics,
      'collectionTotalDetailsByTimeFilterClicksQueryExecutionId',
      ''
    ),
    totalFilterClicksCompared_QueryExecutionId: _.get(
      state.analytics,
      'collectionTotalDetailsByTimeFilterClicksComparedQueryExecutionId',
      ''
    ),

    // Total sales on collection page
    topCollectionsTotalSales_QueryExecutionId: _.get(
      state.analytics,
      'topCollectionsTotalSalesQueryExecutionId',
      ''
    ),
    totalSales_QueryExecutionId: _.get(state.analytics, 'totalSalesQueryExecutionId', ''),
    totalSalesCompared_QueryExecutionId: _.get(
      state.analytics,
      'totalSalesComparedQueryExecutionId',
      ''
    )
  };
};

export default withI18n()(connect(mapStateToProps, mapDispatchToProps)(Collections));
